import React, { useState, useMemo } from 'react';
import {
  Clock,
  UserPlus,
  Coffee,
  MapPin,
  Home,
  ArrowLeftRight,
  Check,
  X,
  Search,
  Filter,
  ChevronDown,
  Calendar,
  FileText,
  MessageSquare,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Plus,
} from 'lucide-react';
import { REGULARIZATION_TYPES, ATTENDANCE_STATUS, formatDate, generateEmployees } from './attendanceData';
import toast from 'react-hot-toast';


// Request Type Icon Component
const RequestTypeIcon = ({ type }) => {
  const icons = {
    missed_checkin: { icon: Clock, bg: 'bg-blue-100', text: 'text-blue-600' },
    missed_checkout: { icon: Clock, bg: 'bg-blue-100', text: 'text-blue-600' },
    late_arrival: { icon: AlertCircle, bg: 'bg-amber-100', text: 'text-amber-600' },
    early_departure: { icon: ArrowLeftRight, bg: 'bg-orange-100', text: 'text-orange-600' },
    on_duty: { icon: MapPin, bg: 'bg-violet-100', text: 'text-violet-600' },
    work_from_home: { icon: Home, bg: 'bg-emerald-100', text: 'text-emerald-600' },
  };

  const config = icons[type] || icons.missed_checkin;
  const Icon = config.icon;

  return (
    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.bg}`}>
      <Icon size={18} className={config.text} />
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    pending: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', icon: Clock, label: 'Pending' },
    approved: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle2, label: 'Approved' },
    rejected: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', icon: XCircle, label: 'Rejected' },
  };

  const { bg, text, border, icon: Icon, label } = config[status] || config.pending;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${bg} ${text} ${border}`}>
      <Icon size={12} />
      {label}
    </span>
  );
};

// Request Details Modal
const RequestDetailsModal = ({ request, onClose, onApprove, onReject }) => {
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  if (!request) return null;

  const typeConfig = REGULARIZATION_TYPES.find(t => t.id === request.type) || {};

  const handleReject = () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    onReject(request.id, rejectReason);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Regularization Request</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition">
            <X size={18} />
          </button>
        </div>

        <div className="p-5">
          {/* Employee Info */}
          <div className="flex items-center gap-4 mb-5">
            <RequestTypeIcon type={request.type} />
            <div>
              <h4 className="font-semibold text-gray-900">{request.name}</h4>
              <p className="text-sm text-gray-500">{request.empCode} • {request.department}</p>
            </div>
            <div className="ml-auto">
              <StatusBadge status={request.status} />
            </div>
          </div>

          {/* Request Details */}
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Request Type</p>
                  <p className="font-medium text-gray-900">{typeConfig.label}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Date</p>
                  <p className="font-medium text-gray-900">{formatDate(request.date)}</p>
                </div>
                {request.requestedTime && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Requested Time</p>
                    <p className="font-medium text-gray-900">{request.requestedTime}</p>
                  </div>
                )}
                {request.actualTime && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Actual Time</p>
                    <p className="font-medium text-gray-900">{request.actualTime}</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1">Reason</p>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-900">{request.reason}</p>
              </div>
            </div>

            <div className="text-xs text-gray-400">
              Submitted on {new Date(request.createdAt).toLocaleString()}
            </div>

            {request.status === 'approved' && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-sm text-emerald-700">
                  <span className="font-medium">Approved by:</span> {request.approver}
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  on {new Date(request.approvedAt).toLocaleString()}
                </p>
              </div>
            )}

            {request.status === 'rejected' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">
                  <span className="font-medium">Rejected by:</span> {request.approver}
                </p>
                <p className="text-sm text-red-600 mt-1">
                  <span className="font-medium">Reason:</span> {request.rejectionReason}
                </p>
              </div>
            )}

            {request.status === 'pending' && showRejectForm && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Rejection Reason</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  placeholder="Please provide a reason for rejection..."
                />
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
          >
            Close
          </button>

          {request.status === 'pending' && (
            <>
              {showRejectForm ? (
                <>
                  <button
                    onClick={() => setShowRejectForm(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReject}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium flex items-center gap-2"
                  >
                    <X size={14} />
                    Confirm Reject
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setShowRejectForm(true)}
                    className="px-4 py-2 bg-white border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition text-sm font-medium flex items-center gap-2"
                  >
                    <X size={14} />
                    Reject
                  </button>
                  <button
                    onClick={() => onApprove(request.id)}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition text-sm font-medium flex items-center gap-2"
                  >
                    <Check size={14} />
                    Approve
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// New Regularization Request Modal
const NewRequestModal = ({ employees, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    employeeId: '',
    type: '',
    date: new Date().toISOString().split('T')[0],
    requestedTime: '',
    reason: '',
  });

  const handleSubmit = () => {
    if (!formData.employeeId || !formData.type || !formData.date || !formData.reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    const employee = employees.find(e => e.id === parseInt(formData.employeeId));
    onSubmit({
      ...formData,
      empCode: employee?.empCode,
      name: employee?.name,
      department: employee?.department,
      status: 'pending',
      createdAt: new Date().toISOString(),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">New Regularization Request</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
            <select
              value={formData.employeeId}
              onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select employee...</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.name} ({emp.empCode})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Request Type *</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select type...</option>
              {REGULARIZATION_TYPES.map(type => (
                <option key={type.id} value={type.id}>{type.label}</option>
              ))}
            </select>
            {formData.type && (
              <p className="text-xs text-gray-500 mt-1">
                {REGULARIZATION_TYPES.find(t => t.id === formData.type)?.description}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
              <input
                type="time"
                value={formData.requestedTime}
                onChange={(e) => setFormData({ ...formData, requestedTime: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Explain why you need this regularization..."
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
          >
            Submit Request
          </button>
        </div>
      </div>
    </div>
  );
};

// Request Card Component
const RequestCard = ({ request, onClick, onApprove, onReject }) => {
  const typeConfig = REGULARIZATION_TYPES.find(t => t.id === request.type) || {};

  return (
    <div
      className="px-4 py-4 hover:bg-gray-50 transition cursor-pointer"
      onClick={() => onClick(request)}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <RequestTypeIcon type={request.type} />
          <div>
            <div className="flex items-center gap-2 mb-1">
              <p className="font-medium text-gray-900">{request.name}</p>
              <StatusBadge status={request.status} />
            </div>
            <p className="text-sm text-gray-500">
              {typeConfig.label} • {formatDate(request.date)}
            </p>
            <p className="text-xs text-gray-400 mt-1 line-clamp-1">{request.reason}</p>
          </div>
        </div>

        {request.status === 'pending' && (
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => onApprove(request.id)}
              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
              title="Approve"
            >
              <Check size={18} />
            </button>
            <button
              onClick={() => onReject(request.id, 'Rejected by manager')}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
              title="Reject"
            >
              <X size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Component
export default function RegularizationRequests({
  
  requests,
  onApprove,
  onReject,
  onNewRequest,
}) {

  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);

  const employees = generateEmployees();

  // Calculate stats
  const stats = useMemo(() => ({
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
  }), [requests]);

  // Filter requests
  const filteredRequests = useMemo(() => {
    let data = [...requests];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      data = data.filter(r =>
        r.name.toLowerCase().includes(query) ||
        r.empCode.toLowerCase().includes(query)
      );
    }

    if (filterStatus !== 'all') {
      data = data.filter(r => r.status === filterStatus);
    }

    if (filterType !== 'all') {
      data = data.filter(r => r.type === filterType);
    }

    // Sort by date descending
    data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return data;
  }, [requests, searchQuery, filterStatus, filterType]);

  const handleApprove = (id) => {
    onApprove(id);
    toast.success('Request approved successfully');
    setSelectedRequest(null);
  };

  const handleReject = (id, reason) => {
    onReject(id, reason);
    toast.success('Request rejected');
    setSelectedRequest(null);
  };

  const handleNewRequest = (data) => {
    onNewRequest(data);
    toast.success('Regularization request submitted');
    setShowNewModal(false);
  };

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    ...REGULARIZATION_TYPES.map(t => ({ value: t.id, label: t.label })),
  ];

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <FileText size={20} className="text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-500">Total Requests</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              <p className="text-sm text-gray-500">Pending</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle2 size={20} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.approved}</p>
              <p className="text-sm text-gray-500">Approved</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.rejected}</p>
              <p className="text-sm text-gray-500">Rejected</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Actions */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-56 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {typeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <button
          onClick={() => setShowNewModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
        >
          <Plus size={16} />
          New Request
        </button>
          
        </div>

      </div>

      {/* Requests List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Regularization Requests</h3>
          <span className="text-sm text-gray-500">{filteredRequests.length} requests</span>
        </div>

        {filteredRequests.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {filteredRequests.map((request) => (
             <RequestCard
              key={request.id}
              request={request}
              onClick={setSelectedRequest}
              onApprove={handleApprove}
              onReject={handleReject}
            />
            ))}
          </div>
        ) : (
          <div className="py-12 text-center">
            <Clock size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No regularization requests found</p>
            <p className="text-sm text-gray-400 mt-1">Requests will appear here when submitted</p>
          </div>
        )}
      </div>

      {/* Request Type Guide */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Request Types</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {REGULARIZATION_TYPES.map((type) => (
            <div key={type.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <RequestTypeIcon type={type.id} />
              <div>
                <p className="font-medium text-gray-900">{type.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{type.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modals */}
      {selectedRequest && (
        <RequestDetailsModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}

      {showNewModal && (
        <NewRequestModal
          employees={employees}
          onClose={() => setShowNewModal(false)}
          onSubmit={handleNewRequest}
        />
      )}
    </div>
  );
}
